import { FormControlLabel, Switch } from '@mui/material';
import React from 'react';

interface ToggleFieldProps {
  /** What the switch controls, e.g. "Verified". Always present: a bare switch says nothing. */
  label: string;
  checked: boolean;
  /** Receives the new value, not the event: every call site was reading e.target.checked. */
  onChange: (_checked: boolean) => void;
  disabled?: boolean;
  /** `small` inside a dense table row; the default everywhere else. */
  size?: 'small' | 'medium';
  /** Explains what turning it on does. Rendered by the caller today; see the note below. */
  name?: string;
}

/**
 * A labelled on/off control.
 *
 * The five input primitives in `Common/` cover text, select, autocomplete, workspace and search,
 * and there was no wrapper for a switch at all, so all thirty call sites wrote the same three
 * lines by hand:
 *
 *   &lt;FormControlLabel control={&lt;Switch checked={x} onChange={(e) =&gt; set(e.target.checked)} /&gt;} label="Y" /&gt;
 *
 * That is fine until one of them differs, and they already do: some pass `size="small"` and some
 * do not, on rows of the same density. A wrapper is the only place a decision like that can be
 * made once.
 *
 * `onChange` hands over the boolean rather than the event, because reaching into
 * `e.target.checked` at thirty call sites is thirty chances to read `e.target.value` instead,
 * which on a checkbox is the string "on" and is always truthy.
 */
const ToggleField: React.FC<ToggleFieldProps> = ({
  label,
  checked,
  onChange,
  disabled = false,
  size = 'medium',
  name,
}) => (
  <FormControlLabel
    label={label}
    disabled={disabled}
    control={
      <Switch
        name={name}
        size={size}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    }
  />
);

export default ToggleField;
